import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Database, Download, ShieldCheck } from 'lucide-react';
import { Button, PageHeader } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/axios';
import { getSauvegardeInfo, telechargerSauvegarde } from '@/services/sauvegarde';
import { toast } from '@/store/toastStore';

function formatOctets(n?: number): string {
  if (!n) return '—';
  const u = ['o', 'Ko', 'Mo', 'Go']; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export function SauvegardePage() {
  const { data: info } = useQuery({ queryKey: ['sauvegarde-info'], queryFn: getSauvegardeInfo });
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try { await telechargerSauvegarde(); toast.success('Sauvegarde téléchargée.'); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setDownloading(false); }
  };

  return (
    <>
      <PageHeader title="Sauvegarde" subtitle="Exporter une sauvegarde complète de la base de données" breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Sauvegarde' }]} />

      <div className="max-w-3xl space-y-5">
        <div className="rounded-lg border border-border bg-surface-secondary p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-brand-primary/10 text-brand-primary"><Database className="size-6" /></span>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-content-primary">Sauvegarde de la base de données</h3>
              <p className="mt-1 text-sm text-content-secondary">Téléchargez un instantané complet (toutes les tables et données) au format SQL compressé. Conservez-le en lieu sûr ; il permet de restaurer l'intégralité de l'ERP.</p>
              <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                <div><dt className="text-content-muted">Base</dt><dd className="font-medium text-content-primary">{info?.base ?? '—'}</dd></div>
                <div><dt className="text-content-muted">Taille</dt><dd className="font-medium text-content-primary">{formatOctets(info?.taille_octets)}</dd></div>
                <div><dt className="text-content-muted">Moteur</dt><dd className="font-medium text-content-primary">{info?.version ?? (info?.postgresql ? 'PostgreSQL' : '—')}</dd></div>
              </dl>
              <div className="mt-5">
                <Button onClick={download} loading={downloading} disabled={info ? !info.sauvegarde_possible : false}>
                  <Download className="size-4" /> Télécharger une sauvegarde
                </Button>
                {info && !info.sauvegarde_possible && <p className="mt-2 text-xs text-status-stuck">La sauvegarde n'est disponible que sur une base PostgreSQL.</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-surface-secondary p-5 text-sm shadow-sm">
          <h4 className="flex items-center gap-2 font-semibold text-content-primary"><ShieldCheck className="size-4 text-status-done" /> Bonnes pratiques</h4>
          <ul className="mt-2 list-inside list-disc space-y-1 text-content-secondary">
            <li>Téléchargez une sauvegarde régulièrement (ex. chaque semaine) et avant toute opération sensible.</li>
            <li>Conservez les fichiers hors du serveur (disque externe, cloud privé).</li>
            <li>Le fichier <code className="rounded bg-surface-primary px-1">.sql.gz</code> se restaure côté serveur avec&nbsp;: <code className="rounded bg-surface-primary px-1">gunzip -c sauvegarde.sql.gz | psql</code>.</li>
          </ul>
        </div>
      </div>
    </>
  );
}
